import { useEffect, useState } from "react";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { EntityType } from "@common/Common.types";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types";
import { supportsTextSelection } from "@common/components/documentPreview/DocumentPreview.hooks";
import { publishStageDocumentOverlay } from "../../main/panes/documentPane/stageDocumentOverlay";
import { usePanes } from "../../main/panes/usePanes";
import DocumentPane from "../../main/panes/documentPane/DocumentPane";
import { ActivityFormProps } from "./ActivityForm.types";
import { ActivityDocumentPreviewMode, ObligationDocumentOption } from "./ActivityContractSourceSection.types";

export type ActivityContractSourceSectionProps = Pick<ActivityFormProps, "model" | "onModelChange" | "readOnly" | "hideContractSourcePreviewActions">;

export function useActivityContractSourceSection(props: ActivityContractSourceSectionProps) {
    const model = props.model;
    const { translateString } = useTranslation();
    const { open: openPane, closeSlot } = usePanes();
    const [previewMode, setPreviewMode] = useState<ActivityDocumentPreviewMode | undefined>(undefined);

    const { data, isLoading } = useDotLegalQuery<Array<ObligationDocumentOption>>(`/contract/${model.contractId}/documents/obligation-options`);
    const options = data ?? [];
    const selectedOption = options.find((option) => option.contractDocumentId === model.contractDocumentId);

    const documentOptions: Array<DotLegalSelectOption> = options.map((option) => ({
        id: option.contractDocumentId,
        name: option.name,
        disabled: !option.hasAccess,
        hoverText: !option.hasAccess ? translateString("obligationDocumentNoAccess") : option.isLink ? translateString("obligationDocumentIsLink") : undefined,
    }));

    const onDocumentChange = (contractDocumentId: string | null) => {
        // Section and clause point into the document, so a different document invalidates them.
        props.onModelChange({ contractDocumentId: contractDocumentId ?? undefined, section: undefined, clause: undefined });
    };

    const onSectionChange = (section: string) => props.onModelChange({ section });

    const onClauseChange = (clause: string) => props.onModelChange({ clause });

    // A link document has no file on our side to render, and an inaccessible one is refused by the server
    // anyway, so neither can be read in the previewer. Both buttons here work on the document's text -
    // selecting a clause, or highlighting one already picked - so the file type has to be one whose viewer
    // can reach that text, which is narrower than what the previewer will open.
    const isPreviewable =
        selectedOption !== undefined && selectedOption.hasAccess && !selectedOption.isLink && supportsTextSelection(selectedOption.fileName ?? "");

    // Why the previewer is unavailable, for a tooltip on the disabled buttons: the document being a
    // spreadsheet or being restricted is not something the user can work out from a dead button.
    const getPreviewUnavailableReason = () => {
        if (!selectedOption || isPreviewable) {
            return undefined;
        }
        if (!selectedOption.hasAccess) {
            return translateString("obligationDocumentNoAccess");
        }
        return translateString("obligationDocumentFileTypeNotSupported");
    };

    const previewDocument: PreviewableDocument | undefined =
        isPreviewable && selectedOption
            ? {
                  documentId: selectedOption.documentId,
                  documentVersionId: selectedOption.documentVersionId,
                  dataId: selectedOption.dataId,
                  fileName: selectedOption.fileName,
                  name: selectedOption.name,
                  entityType: EntityType.Contract,
                  entityId: model.contractId!,
              }
            : undefined;

    const closePreview = () => {
        setPreviewMode(undefined);
        closeSlot("stage");
    };

    const showPreview = (mode: ActivityDocumentPreviewMode) => {
        setPreviewMode(mode);
        openPane(DocumentPane, {
            documents: [previewDocument!],
            initialDocumentId: previewDocument!.documentVersionId,
            citationNotFoundKey: "clauseNotFoundInDocument",
        });
    };

    // What the document on the stage should highlight, and whether picking text in it has somewhere to go.
    // Republished only when one of those actually changes: the channel notifies its listeners on every
    // publish, so publishing a fresh object each render would re-render the pane without end.
    useEffect(() => {
        if (!previewMode || !previewDocument) {
            return;
        }
        publishStageDocumentOverlay({
            documentId: previewDocument.documentId,
            citationChunkContent: previewMode === "highlight" ? model.clause : undefined,
            textSelection:
                previewMode === "select"
                    ? {
                          onConfirm: onSelectedTextConfirmed,
                          actionLabel: translateString("useAsClause"),
                          hint: translateString("selectTextHint"),
                      }
                    : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewMode, previewDocument?.documentId, model.clause, model.contractDocumentId]);

    useEffect(() => () => publishStageDocumentOverlay(undefined), []);

    const onSelectedTextConfirmed = (result: { clause: string; section?: string }) => {
        closePreview();
        // A section the heuristic could not guess is left empty rather than keeping a stale one from an
        // earlier selection.
        props.onModelChange({ section: result.section ?? "", clause: result.clause });
    };

    return {
        isLoading,
        documentOptions,
        selectedOption,
        openTextSelection: () => showPreview("select"),
        openClausePreview: () => showPreview("highlight"),
        onDocumentChange,
        onSectionChange,
        onClauseChange,
        canOpenDocument: isPreviewable,
        // Previewing needs something to highlight. Until then the button is not disabled but absent:
        // there is nothing to explain, and a control that is dead on arrival teaches the user nothing.
        showPreviewClause: (model.clause ?? "").trim().length > 0,
        previewUnavailableReason: getPreviewUnavailableReason(),
    };
}
